import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SafeStorage } from '../utils/safeStorage';
import backgroundPattern from '../assets/8f320715a0789cd66ecbac8ed3c33f41f7bf02e3.png';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Load theme from localStorage on initial mount
    if (typeof window !== 'undefined') {
      const savedTheme = SafeStorage.getItem('afroyap-theme') as Theme;
      return savedTheme || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    // Apply theme to document root
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Save theme to localStorage
    SafeStorage.setItem('afroyap-theme', theme);

    // Always set the background pattern URL (it will be conditionally used by components)
    // root.style.setProperty('--bg-pattern-url', `url(${backgroundPattern})`);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    // Fallback for HMR race conditions or components rendered transiently
    // outside the provider tree. Returns a safe default instead of crashing.
    console.warn('[useTheme] Called outside ThemeProvider — returning default dark theme.');
    return { theme: 'dark' as const, toggleTheme: () => {} };
  }
  return context;
}
